import { HStack, FormRow } from '@cad-arc';
import { FormInput } from '../../../../forms/integrated-form-controls/form-input';
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import { FirearmsBaseKey } from '../types';
import { FormSelect } from '../../../../forms/integrated-form-controls/form-select';
import { FormCheckbox } from '../../../../forms/integrated-form-controls/form-checkbox';
import { FormTextarea } from '../../../../forms/integrated-form-controls/form-text-area';
import { getUniversalSearchLabel } from '../../../../common/helpers/get-usx-labels';
import { useEnabledFeatures } from '../../../../common/hooks/enabled-features';
import { useIsClearedEvent } from '../../use-is-cleared-event';
import { useIsDexEnabled } from '../../../../data-exchange/use-dex-access-guard';

export function EventPanelFormFirearmPropertyInputs(props: {
    form: UseFormReturn<EventPanelFormType>;
    index: number;
    baseKey: FirearmsBaseKey;
    eventPanelOptions: EventPanelOptions;
    showExtraFields: boolean;
    isPersisted: boolean;
}) {
    const {
        form: eventForm,
        index,
        baseKey,
        showExtraFields,
        eventPanelOptions,
        isPersisted,
    } = props;

    const { dexNextEnabled } = useEnabledFeatures();
    const { isDexEnabled } = useIsDexEnabled();
    const isClearedEvent = useIsClearedEvent();

    React.useEffect(
        function disableQueryInDexWhenDexIsBlocked() {
            if (!isDexEnabled) {
                eventForm.setValue(`${baseKey}.${index}.queryInDex`, false);
            }
        },
        [baseKey, eventForm, index, isDexEnabled]
    );

    return (
        <>
            <FormRow data-testid="firearms-section">
                <FormInput
                    control={eventForm.control}
                    label="Serial #"
                    name={`${baseKey}.${index}.serialNumber`}
                    asEditable={!!isPersisted}
                    length="md"
                    eventNotificationName="FIREARM_SERIAL_NUMBER"
                />
                <FormSelect
                    length="sm"
                    control={eventForm.control}
                    label="Firearm Make"
                    name={`${baseKey}.${index}.firearmMake`}
                    asEditable={!!isPersisted}
                    options={eventPanelOptions.firearmMakeOptions}
                    eventNotificationName="FIREARM_MAKE"
                />
                <FormSelect
                    length="sm"
                    control={eventForm.control}
                    label="Caliber"
                    name={`${baseKey}.${index}.caliber`}
                    options={eventPanelOptions.ncicFirearmCaliberOptions}
                    asEditable={!!isPersisted}
                    eventNotificationName="FIREARM_CALIBER"
                />
            </FormRow>
            {!isPersisted && isDexEnabled && (
                <FormRow>
                    <FormCheckbox
                        label={`Query in ${getUniversalSearchLabel(dexNextEnabled)}`}
                        name={`${baseKey}.${index}.queryInDex`}
                        control={props.form.control}
                        asEditable={false}
                        length="sm"
                        size="sm"
                        isDisabled={isClearedEvent}
                        checkboxProps={
                            isClearedEvent ? { sx: { cursor: 'not-allowed' } } : undefined
                        }
                    />
                </FormRow>
            )}
            {showExtraFields && (
                <>
                    <FormTextarea
                        control={eventForm.control}
                        label="Description"
                        name={`${props.baseKey}.${index}.description`}
                        length="md"
                        asEditable={!!isPersisted}
                        eventNotificationName="FIREARM_DESCRIPTION"
                    />
                    <FormRow>
                        <FormInput
                            control={eventForm.control}
                            label="Registration #"
                            name={`${props.baseKey}.${index}.registrationNumber`}
                            length="sm"
                            asEditable={!!isPersisted}
                            eventNotificationName="FIREARM_REGISTRATION_NUMBER"
                        />
                        <FormSelect
                            length="sm"
                            control={eventForm.control}
                            label="Firearm Type"
                            name={`${baseKey}.${index}.firearmType`}
                            asEditable={!!isPersisted}
                            options={eventPanelOptions.firearmTypeOptions}
                            eventNotificationName="FIREARM_FIREARM_TYPE"
                        />
                        <HStack marginTop={4} width={150}>
                            <FormCheckbox
                                label="Alteration Indicated"
                                name={`${baseKey}.${index}.alterationIndicated`}
                                control={props.form.control}
                                asEditable={false}
                                length="md"
                                size="sm"
                                eventNotificationName="FIREARM_ALTERATION_INDICATED"
                            />
                        </HStack>
                    </FormRow>
                    <FormRow>
                        <FormSelect
                            control={eventForm.control}
                            label="Identifier Type"
                            name={`${props.baseKey}.${index}.identifierType`}
                            length="md"
                            options={eventPanelOptions.itemIdentifierOptions}
                            asEditable={!!isPersisted}
                            eventNotificationName="FIREARM_IDENTIFIER_TYPE"
                        />
                        <FormInput
                            control={eventForm.control}
                            label="Identifier"
                            name={`${props.baseKey}.${index}.identifier`}
                            length="md"
                            asEditable={!!isPersisted}
                            eventNotificationName="FIREARM_IDENTIFIER"
                        />
                    </FormRow>
                </>
            )}
        </>
    );
}
